/**
 * UsersManagementView — org-scoped firm user management, gated on firm.manage.
 *
 * Organization Admins list their firm's users, add one as a non-privileged
 * account (roles are assigned on the Roles screen), lock or unlock accounts,
 * and issue an invite link that is emailed where the environment allows and
 * shown to copy either way.
 */
import React, { useState } from 'react';
import { FiUserPlus, FiLock, FiUnlock, FiLink, FiCopy, FiCheckCircle, FiAlertTriangle } from 'react-icons/fi';
import { toast } from 'sonner';

import IconWrapper from '../../IconWrapper';
import { Heading } from '../../ui/heading';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../ui/card';
import { useAccess, Capability } from '../../../hooks/useAccess';
import {
  useFirmUsers,
  useCreateFirmUser,
  useSetFirmUserActive,
  useGenerateInviteLink,
} from '../../../hooks/useFirmUsers';

const UsersManagementView: React.FC = () => {
  const { can } = useAccess();
  const canManage = can(Capability.FirmManage);
  const { data: users, isLoading } = useFirmUsers();
  const createUser = useCreateFirmUser();
  const setActive = useSetFirmUserActive();
  const generateLink = useGenerateInviteLink();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [invite, setInvite] = useState<{ link: string; label: string; sent: boolean; emailTo: string | null } | null>(null);
  const outcome = !invite
    ? ''
    : invite.sent
      ? `Sent to ${invite.emailTo}.`
      : invite.emailTo
        ? `The email to ${invite.emailTo} didn't send.`
        : "The email didn't send.";

  const issueLink = async (userId: string, label: string) => {
    try {
      const { link, emailSent, emailTo } = await generateLink.mutateAsync({ userId });
      // The label is a username when the person has no email, so it is no address to name.
      setInvite({ link, label, sent: emailSent, emailTo: emailTo || (label.includes('@') ? label : null) });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to generate invite link');
    }
  };

  const handleCreate = async () => {
    if (!fullName.trim() || !email.trim()) return;
    try {
      const user = await createUser.mutateAsync({
        name: fullName.trim(),
        email: email.trim(),
      });
      toast.success(`User "${fullName.trim()}" created`);
      setFullName('');
      setEmail('');
      // Email the new user their link, and show it for the admin to copy.
      await issueLink(user.userId, user.email || email.trim());
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to create user');
    }
  };

  const handleToggle = async (userId: string, isActive: boolean) => {
    try {
      await setActive.mutateAsync({ userId, isActive: !isActive });
      toast.success(isActive ? 'User locked' : 'User unlocked');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to update user');
    }
  };

  const copyInvite = async () => {
    if (!invite) return;
    try {
      await navigator.clipboard.writeText(invite.link);
      toast.success('Invite link copied to clipboard');
    } catch {
      toast.error('Could not copy — select the link and copy manually');
    }
  };

  if (!canManage) {
    return (
      <div className="p-6">
        <Heading level={2}>Users</Heading>
        <p className="mt-2 text-sm text-muted-foreground">
          You need organization administration access to manage users.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <Heading level={2}>Users</Heading>
        <p className="mt-1 text-sm text-muted-foreground">
          Manage the people in your organization. Assign roles on the Roles screen.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Add a user</CardTitle>
          <CardDescription>
            Creates a standard account in your organization — grant roles afterwards.
            We&apos;ll email them a link to set a password, and show it here to copy.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Input
            placeholder="Full Name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
          <Input
            placeholder="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          />
          <Button
            onClick={handleCreate}
            loading={createUser.isPending || generateLink.isPending}
            disabled={!fullName.trim() || !email.trim()}
            leftIcon={<IconWrapper icon={FiUserPlus} />}
          >
            Add user
          </Button>
        </CardContent>
      </Card>

      {invite && (
        <Card>
          <CardHeader>
            <CardTitle>Invite link for {invite.label}</CardTitle>
          </CardHeader>
          <CardContent className="pb-0">
            {invite.sent ? (
              <div className="flex items-start gap-2 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                <IconWrapper icon={FiCheckCircle} className="h-5 w-5 shrink-0" />
                <span>{outcome}</span>
              </div>
            ) : (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <IconWrapper icon={FiAlertTriangle} className="h-5 w-5 shrink-0" />
                <span>{outcome} Copy the link below and send it to them yourself.</span>
              </div>
            )}
          </CardContent>
          <CardContent className="flex flex-wrap items-center gap-2">
            <Input
              readOnly
              value={invite.link}
              className="flex-1 min-w-[240px]"
              onFocus={(e) => e.currentTarget.select()}
            />
            <Button onClick={copyInvite} leftIcon={<IconWrapper icon={FiCopy} />}>
              Copy
            </Button>
            <Button variant="outline" onClick={() => setInvite(null)}>
              Done
            </Button>
            <p className="w-full text-sm text-muted-foreground">The link works for 24 hours.</p>
          </CardContent>
        </Card>
      )}
      {/* Mounted throughout, so the outcome is announced when it arrives. */}
      <p className="sr-only" role="status">{outcome}</p>

      <div className="space-y-2">
        {isLoading && (
          <p className="text-sm text-muted-foreground">Loading users…</p>
        )}
        {users?.map((user) => (
          <Card key={user.userId}>
            <CardContent className="flex items-center justify-between py-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{user.name || user.username}</span>
                  {!user.isActive && <Badge variant="destructive">Locked</Badge>}
                </div>
                {user.email && (
                  <p className="text-sm text-muted-foreground">{user.email}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => issueLink(user.userId, user.email || user.username)}
                  loading={generateLink.isPending}
                  leftIcon={<IconWrapper icon={FiLink} />}
                >
                  Invite link
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleToggle(user.userId, user.isActive)}
                  loading={setActive.isPending}
                  leftIcon={
                    <IconWrapper icon={user.isActive ? FiLock : FiUnlock} />
                  }
                >
                  {user.isActive ? 'Lock' : 'Unlock'}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default UsersManagementView;
